import type { AmountMode, InvoiceDraft, InvoiceLabels, InvoiceLine } from '../../types';
import { currencySymbol } from './currencies';

export const DEFAULT_LABELS: InvoiceLabels = {
  title: 'INVOICE',
  billTo: 'Bill To',
  shipTo: 'Ship To',
  date: 'Date',
  paymentTerms: 'Payment Terms',
  dueDate: 'Due Date',
  poNumber: 'PO Number',
  item: 'Item',
  quantity: 'Quantity',
  rate: 'Rate',
  amount: 'Amount',
  notes: 'Notes',
  terms: 'Terms',
  subtotal: 'Subtotal',
  tax: 'Tax',
  discount: 'Discount',
  shipping: 'Shipping',
  total: 'Total',
  amountPaid: 'Amount Paid',
  balanceDue: 'Balance Due',
};

export function lineAmount(item: InvoiceLine): number {
  return (Number(item.qty) || 0) * (Number(item.unitPrice) || 0);
}

export function applyAmountMode(
  base: number,
  mode: AmountMode,
  value: number,
): number {
  if (mode === 'off') return 0;
  if (mode === 'percent') return base * ((Number(value) || 0) / 100);
  return Number(value) || 0;
}

export function invoiceTotals(invoice: InvoiceDraft) {
  const sub = invoice.items.reduce((sum, item) => sum + lineAmount(item), 0);
  const discount = applyAmountMode(
    sub,
    invoice.discountMode,
    invoice.discountValue,
  );
  const afterDiscount = sub - discount;
  const tax = applyAmountMode(afterDiscount, invoice.taxMode, invoice.taxValue);
  const shipping =
    invoice.shippingMode === 'flat' ? Number(invoice.shippingValue) || 0 : 0;
  const total = afterDiscount + tax + shipping;
  const amountPaid = Number(invoice.amountPaid) || 0;
  const balanceDue = total - amountPaid;
  return { subtotal: sub, discount, tax, shipping, total, amountPaid, balanceDue };
}

export function formatStandardMoney(amount: number, currencyCode: string): string {
  const symbol = currencySymbol(currencyCode);
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = amount < 0 ? '-' : '';
  if (symbol.length === 1 || symbol === '$' || symbol === '€' || symbol === '£') {
    return `${sign}${symbol}${formatted}`;
  }
  return `${sign}${formatted} ${symbol}`;
}
